import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, throwError } from 'rxjs';
import { switchMap, filter, take, catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { EnvironmentUrl } from './shared/services/environment-url';

// ── Module-level state for concurrent refresh queuing ──────────────────────
// Lives outside the function — persists across interceptor calls
let isRefreshing = false;
let refreshToken$ = new BehaviorSubject<string | null>(null);

// ── Auth interceptor function ──────────────────────────────────────────────
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const apiUrl = inject(EnvironmentUrl).urlAddress;

  // Skip auth endpoints (login, refresh, logout) — they don't need Bearer tokens
  if (isAuthEndpoint(req.url, apiUrl)) {
    return next(req.clone({ withCredentials: true }));
  }

  // Add Bearer token to the request
  const token = auth.accessToken();
  const authReq = token ? addToken(req, token) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) return throwError(() => error);

      if (isRefreshing) {
        // Another request is already refreshing — queue this one
        return refreshToken$.pipe(
          filter((t) => t !== null), // wait for the new token
          take(1),
          switchMap((newToken) => next(addToken(req, newToken!))),
        );
      }

      // Start the refresh
      isRefreshing = true;
      refreshToken$.next(null); // signal "refresh in progress"

      return auth.refreshToken().pipe(
        switchMap((newToken) => {
          isRefreshing = false;
          refreshToken$.next(newToken); // broadcast to queued requests
          return next(addToken(req, newToken));
        }),
        catchError((refreshError) => {
          isRefreshing = false;
          refreshToken$.next(null);
          // Refresh failed — clear session and redirect to login
          auth.clearSession(); // make this method public in AuthService
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────
function addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
    withCredentials: true, // send httpOnly cookies (refresh token)
  });
}

function isAuthEndpoint(url: string, apiUrl: string): boolean {
  const authPaths = ['/api/auth/login', '/api/auth/refresh', '/api/auth/logout'];
  return authPaths.some((path) => url.includes(path));
}
